import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { Typography } from "@mui/material";
import { defineMessages, useIntl } from "react-intl";

enum ReasoningEffort {
  Minimal = "minimal",
  Low = "low",
  Medium = "medium",
  High = "high",
}

export const defaultReasoningEffort = ReasoningEffort.Minimal;

const reasoningEffortLabels = defineMessages({
  [ReasoningEffort.Minimal]: {
    id: "reasoningEffort.minimal",
    defaultMessage: "Minimal (fast)",
  },
  [ReasoningEffort.Low]: {
    id: "reasoningEffort.low",
    defaultMessage: "Low",
  },
  [ReasoningEffort.Medium]: {
    id: "reasoningEffort.medium",
    defaultMessage: "Medium",
  },
  [ReasoningEffort.High]: {
    id: "reasoningEffort.high",
    defaultMessage: "High (slow)",
  },
});

const messages = defineMessages({
  reasoningEffort: {
    id: "reasoningEffort",
    defaultMessage: "Thinking Effort",
  },
});

const supportedReasoningEfforts = [
  {
    value: "minimal",
    label: reasoningEffortLabels[ReasoningEffort.Minimal],
  },
  {
    value: "low",
    label: reasoningEffortLabels[ReasoningEffort.Low],
  },
  {
    value: "medium",
    label: reasoningEffortLabels[ReasoningEffort.Medium],
  },
  {
    value: "high",
    label: reasoningEffortLabels[ReasoningEffort.High],
  },
];

export function getReasoningEffortLabel(reasoningEffort: string) {
  return supportedReasoningEfforts.find((t) => t.value === reasoningEffort)?.label;
}

export function ReasoningEffortSelector(props: Props) {
  return (
    <>
      <ReasoningEffortHeading />
      <ReasoningEffortSlider {...props} />
    </>
  );
}

export function ReasoningEffortHeading() {
  const { formatMessage: t } = useIntl();
  return (
    <Typography variant="h6" id="reasoning-effort-slider" gutterBottom>
      {t(messages.reasoningEffort)}
    </Typography>
  );
}

type Props = {
  reasoningEffort?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

export function ReasoningEffortSlider({ reasoningEffort, onChange, disabled }: Props) {
  const { formatMessage: t } = useIntl();

  const getReasoningEffortName = (value: string) => {
    if (value in ReasoningEffort) {
      return t(reasoningEffortLabels[value as ReasoningEffort]);
    }
    return "";
  };

  const handleChange = (_: Event, newValue: number | number[]) => {
    const stringValue = supportedReasoningEfforts[newValue as number].value;
    onChange(stringValue);
  };

  const valueIndex = supportedReasoningEfforts.findIndex(
    ({ value }) => value === reasoningEffort
  );
  const value = valueIndex >= 0 ? valueIndex : 0; // default to minimal

  return (
    <>
      <Box sx={{ width: "auto", mx: 4 }}>
        <Slider
          aria-label="Reasoning effort values"
          defaultValue={0}
          valueLabelDisplay="off"
          getAriaValueText={(value) => getReasoningEffortName(supportedReasoningEfforts[value].value)}
          step={1}
          onChange={handleChange}
          value={value}
          disabled={disabled}
          marks={supportedReasoningEfforts.map(({ label }, index) => ({
            value: index,
            label: t(label),
          }))}
          min={0}
          max={3}
        />
      </Box>
    </>
  );
}
